"use client";

import * as React from "react";
import type { Theme } from "@mui/material/styles";
import { useColorScheme } from "@mui/material/styles";
import {
  normalizeSyntaxHighlightThemeId,
  type SyntaxHighlightThemeId,
} from "@/lib/syntaxHighlightTheme";
import {
  atomDark,
  darcula,
  dracula,
  duotoneDark,
  duotoneLight,
  gruvboxDark,
  gruvboxLight,
  materialDark,
  materialLight,
  nightOwl,
  nord,
  okaidia,
  oneDark,
  oneLight,
  solarizedlight,
  tomorrow,
  vs,
  vscDarkPlus,
} from "react-syntax-highlighter/dist/esm/styles/prism";

export type PrismStyleMap = { [key: string]: React.CSSProperties };
const SYNTAX_HIGHLIGHT_THEME_EVENT = "pcubed:syntax-highlight-theme-change";
const SYNTAX_HIGHLIGHT_THEME_PREVIEW_EVENT =
  "pcubed:syntax-highlight-theme-preview-change";

const PRISM_STYLE_BY_THEME_ID: Record<
  Exclude<SyntaxHighlightThemeId, "adaptive">,
  PrismStyleMap
> = {
  vscDarkPlus: vscDarkPlus as PrismStyleMap,
  oneDark: oneDark as PrismStyleMap,
  oneLight: oneLight as PrismStyleMap,
  nightOwl: nightOwl as PrismStyleMap,
  nord: nord as PrismStyleMap,
  dracula: dracula as PrismStyleMap,
  atomDark: atomDark as PrismStyleMap,
  okaidia: okaidia as PrismStyleMap,
  materialDark: materialDark as PrismStyleMap,
  materialLight: materialLight as PrismStyleMap,
  darcula: darcula as PrismStyleMap,
  duotoneDark: duotoneDark as PrismStyleMap,
  duotoneLight: duotoneLight as PrismStyleMap,
  gruvboxDark: gruvboxDark as PrismStyleMap,
  gruvboxLight: gruvboxLight as PrismStyleMap,
  solarizedLight: solarizedlight as PrismStyleMap,
  tomorrow: tomorrow as PrismStyleMap,
  vs: vs as PrismStyleMap,
};

function buildAdaptivePrismStyle(theme: Theme): PrismStyleMap {
  const palette = theme.vars?.palette ?? theme.palette;

  return {
    'code[class*="language-"]': {
      color: palette.text.primary,
      background: "transparent",
      fontFamily:
        '"IBM Plex Mono", "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: "inherit",
      lineHeight: "inherit",
      textAlign: "left",
      whiteSpace: "pre",
      wordSpacing: "normal",
      wordBreak: "normal",
      wordWrap: "normal",
      tabSize: 2,
      hyphens: "none",
    },
    'pre[class*="language-"]': {
      color: palette.text.primary,
      background: "transparent",
      margin: 0,
      padding: 0,
      fontSize: "inherit",
      lineHeight: "inherit",
      overflow: "visible",
    },
    comment: {
      color: palette.text.secondary,
      fontStyle: "italic",
    },
    prolog: {
      color: palette.text.secondary,
    },
    doctype: {
      color: palette.text.secondary,
    },
    cdata: {
      color: palette.text.secondary,
    },
    punctuation: {
      color: palette.text.primary,
    },
    operator: {
      color: palette.text.primary,
    },
    keyword: {
      color: palette.primary.main,
    },
    atrule: {
      color: palette.primary.main,
    },
    builtin: {
      color: palette.info.main,
    },
    function: {
      color: palette.info.main,
    },
    "class-name": {
      color: palette.info.main,
    },
    string: {
      color: palette.success.main,
    },
    char: {
      color: palette.success.main,
    },
    regex: {
      color: palette.success.main,
    },
    number: {
      color: palette.warning.main,
    },
    boolean: {
      color: palette.warning.main,
    },
    constant: {
      color: palette.warning.main,
    },
    property: {
      color: palette.secondary.main,
    },
    variable: {
      color: palette.secondary.main,
    },
    symbol: {
      color: palette.secondary.main,
    },
    tag: {
      color: palette.error.main,
    },
    important: {
      color: palette.primary.main,
      fontWeight: 700,
    },
    bold: {
      fontWeight: 700,
    },
    italic: {
      fontStyle: "italic",
    },
  };
}

export function getPrismStyleForSyntaxTheme(
  theme: Theme,
  syntaxThemeId: SyntaxHighlightThemeId,
): PrismStyleMap {
  if (syntaxThemeId === "adaptive") {
    return buildAdaptivePrismStyle(theme);
  }

  return PRISM_STYLE_BY_THEME_ID[syntaxThemeId] ?? buildAdaptivePrismStyle(theme);
}

type SyntaxHighlightThemeState = ReturnType<typeof useSyntaxHighlightThemeState>;

// Only the Provider below should call this; every other consumer must go
// through useSyntaxHighlightTheme() so there is a single source of truth for
// the selected theme instead of each component racing to reset/rebroadcast it.
function useSyntaxHighlightThemeState() {
  const { mode, systemMode } = useColorScheme();
  const preferredSyntaxThemeId: Exclude<SyntaxHighlightThemeId, "adaptive"> =
    mode === "dark" || (mode !== "light" && systemMode === "dark")
      ? "oneDark"
      : "oneLight";
  const [selectedSyntaxThemeId, setSelectedSyntaxThemeId] =
    React.useState<SyntaxHighlightThemeId>(preferredSyntaxThemeId);
  const [previewSyntaxThemeId, setPreviewSyntaxThemeIdState] =
    React.useState<SyntaxHighlightThemeId | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setSelectedSyntaxThemeId(preferredSyntaxThemeId);

    window.dispatchEvent(
      new CustomEvent(SYNTAX_HIGHLIGHT_THEME_EVENT, {
        detail: { syntaxThemeId: preferredSyntaxThemeId },
      }),
    );

    const handleThemeEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ syntaxThemeId?: unknown }>;
      const normalizedThemeId = normalizeSyntaxHighlightThemeId(
        customEvent.detail?.syntaxThemeId,
      );
      setSelectedSyntaxThemeId(normalizedThemeId);
    };

    const handlePreviewThemeEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ syntaxThemeId?: unknown | null }>;
      const nextThemeId = customEvent.detail?.syntaxThemeId;
      if (nextThemeId == null) {
        setPreviewSyntaxThemeIdState(null);
        return;
      }

      setPreviewSyntaxThemeIdState(normalizeSyntaxHighlightThemeId(nextThemeId));
    };

    window.addEventListener(SYNTAX_HIGHLIGHT_THEME_EVENT, handleThemeEvent);
    window.addEventListener(
      SYNTAX_HIGHLIGHT_THEME_PREVIEW_EVENT,
      handlePreviewThemeEvent,
    );

    return () => {
      window.removeEventListener(SYNTAX_HIGHLIGHT_THEME_EVENT, handleThemeEvent);
      window.removeEventListener(
        SYNTAX_HIGHLIGHT_THEME_PREVIEW_EVENT,
        handlePreviewThemeEvent,
      );
    };
  }, [preferredSyntaxThemeId]);

  const setSyntaxThemeId = React.useCallback((nextThemeId: SyntaxHighlightThemeId) => {
    const normalizedThemeId = normalizeSyntaxHighlightThemeId(nextThemeId);
    setSelectedSyntaxThemeId(normalizedThemeId);

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(SYNTAX_HIGHLIGHT_THEME_EVENT, {
          detail: { syntaxThemeId: normalizedThemeId },
        }),
      );
    }

    void fetch("/api/user/syntax-theme", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ syntaxThemeId: normalizedThemeId }),
    }).then((response) => {
      if (!response.ok) throw new Error(`Unable to save syntax theme (${response.status})`);
    }).catch((error: unknown) => {
      console.error("Failed to save syntax theme", error);
    });
  }, []);

  const setPreviewSyntaxThemeId = React.useCallback(
    (nextThemeId: SyntaxHighlightThemeId | null) => {
      if (nextThemeId === null) {
        setPreviewSyntaxThemeIdState(null);

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(SYNTAX_HIGHLIGHT_THEME_PREVIEW_EVENT, {
              detail: { syntaxThemeId: null },
            }),
          );
        }

        return;
      }

      const normalizedThemeId = normalizeSyntaxHighlightThemeId(nextThemeId);
      setPreviewSyntaxThemeIdState(normalizedThemeId);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(SYNTAX_HIGHLIGHT_THEME_PREVIEW_EVENT, {
            detail: { syntaxThemeId: normalizedThemeId },
          }),
        );
      }
    },
    [],
  );

  const clearPreviewSyntaxThemeId = React.useCallback(() => {
    setPreviewSyntaxThemeId(null);
  }, [setPreviewSyntaxThemeId]);

  const syntaxThemeId = previewSyntaxThemeId ?? selectedSyntaxThemeId;

  return {
    syntaxThemeId,
    selectedSyntaxThemeId,
    setSyntaxThemeId,
    setPreviewSyntaxThemeId,
    clearPreviewSyntaxThemeId,
  };
}

const SyntaxHighlightThemeContext =
  React.createContext<SyntaxHighlightThemeState | null>(null);

// Mount once (in the protected layout) so every code renderer reads the same
// selected/preview theme instead of each maintaining its own copy.
export function SyntaxHighlightThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const state = useSyntaxHighlightThemeState();
  return (
    <SyntaxHighlightThemeContext.Provider value={state}>
      {children}
    </SyntaxHighlightThemeContext.Provider>
  );
}

export function useSyntaxHighlightTheme() {
  const context = React.useContext(SyntaxHighlightThemeContext);
  if (!context) {
    throw new Error(
      "useSyntaxHighlightTheme must be used within a SyntaxHighlightThemeProvider",
    );
  }
  return context;
}
